import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface AiSummary {
  overview: string;
  keyTopics: string[];
  intendedAudience: string;
  entitiesMentioned: string[];
  actionItems: string[];
  limitations: string;
  modelId: string;
}

interface AiSummaryModalProps {
  open: boolean;
  title: string;
  aiSummary?: AiSummary | null;
  loading?: boolean;
  onClose: () => void;
}

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', mb: 0.5 }}>
    {label}
  </Typography>
);

const AiSummaryModal: React.FC<AiSummaryModalProps> = ({ open, title, aiSummary, loading = false, onClose }) => {
  const [copied, setCopied] = React.useState(false);

  const buildPlainText = useCallback((): string => {
    if (!aiSummary) return '';
    const lines: string[] = [`AI Summary: ${title}`, ''];
    if (aiSummary.overview) lines.push('Overview', aiSummary.overview, '');
    if (aiSummary.keyTopics?.length) lines.push('Key Topics', aiSummary.keyTopics.join(', '), '');
    if (aiSummary.intendedAudience) lines.push('Intended Audience', aiSummary.intendedAudience, '');
    if (aiSummary.entitiesMentioned?.length) lines.push('Entities Mentioned', aiSummary.entitiesMentioned.join(', '), '');
    if (aiSummary.actionItems?.length) lines.push('Action Items', ...aiSummary.actionItems.map(a => `• ${a}`), '');
    if (aiSummary.limitations) lines.push('Limitations', aiSummary.limitations);
    return lines.join('\n');
  }, [aiSummary, title]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API unavailable — silent fail
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <span>AI Summary</span>
        </Box>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : !aiSummary ? (
          <Alert severity="info" icon={<AutoAwesomeIcon />}>
            AI summarization has not been generated for this content yet. It will be created automatically during AI processing, or you can trigger it from the edit dialog.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Overview */}
            {aiSummary.overview && (
              <Box>
                <SectionLabel label="Overview" />
                <Typography variant="body2">{aiSummary.overview}</Typography>
              </Box>
            )}

            {/* Key Topics */}
            {aiSummary.keyTopics?.length > 0 && (
              <Box>
                <SectionLabel label="Key Topics" />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {aiSummary.keyTopics.map((topic) => (
                    <Chip key={topic} label={topic} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Intended Audience */}
            {aiSummary.intendedAudience && (
              <Box>
                <SectionLabel label="Intended Audience" />
                <Typography variant="body2">{aiSummary.intendedAudience}</Typography>
              </Box>
            )}

            {/* Entities Mentioned */}
            {aiSummary.entitiesMentioned?.length > 0 && (
              <Box>
                <SectionLabel label="Entities Mentioned" />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {aiSummary.entitiesMentioned.map((entity) => (
                    <Chip key={entity} label={entity} size="small" color="default" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Action Items */}
            {aiSummary.actionItems?.length > 0 && (
              <Box>
                <SectionLabel label="Action Items" />
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {aiSummary.actionItems.map((item, i) => (
                    <Typography key={i} component="li" variant="body2" sx={{ mb: 0.25 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Limitations */}
            {aiSummary.limitations && (
              <>
                <Divider />
                <Box>
                  <SectionLabel label="Limitations / Caveats" />
                  <Typography variant="body2" color="text.secondary">{aiSummary.limitations}</Typography>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        {aiSummary?.modelId && (
          <Typography variant="caption" color="text.disabled">
            Generated by {aiSummary.modelId}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          {aiSummary && (
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <Button
                size="small"
                startIcon={<CopyIcon fontSize="small" />}
                onClick={handleCopy}
                color={copied ? 'success' : 'inherit'}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Tooltip>
          )}
          <Button onClick={onClose} size="small">Close</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AiSummaryModal;
